import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Calendar,
  Activity,
  Heart,
  Brain,
  Eye,
  Shield,
  Target,
  BarChart3,
  LineChart,
  PieChart,
  Download,
  Share2,
  Filter,
  Search,
  RefreshCw,
  Settings
} from 'lucide-react';

interface HealthPrediction {
  id: string;
  type: 'risk' | 'opportunity' | 'trend' | 'alert';
  category: 'cardiovascular' | 'diabetes' | 'mental-health' | 'nutrition' | 'fitness' | 'sleep';
  title: string;
  description: string;
  confidence: number; // 0-100
  severity: 'low' | 'medium' | 'high' | 'critical';
  predictedDate: Date;
  currentValue?: number;
  predictedValue?: number;
  unit?: string;
  recommendations: string[];
  dataPoints: Array<{
    date: Date;
    value: number;
  }>;
  isActive: boolean;
  createdAt: Date;
}

interface PredictiveInsightsProps {
  onBack?: () => void;
}

export const PredictiveInsights: React.FC<PredictiveInsightsProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'predictions' | 'analytics' | 'settings'>('overview');
  const [predictions, setPredictions] = useState<HealthPrediction[]>([]);
  const [selectedPrediction, setSelectedPrediction] = useState<HealthPrediction | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  // Mock predictions data
  useEffect(() => {
    const mockPredictions: HealthPrediction[] = [
      {
        id: '1',
        type: 'risk',
        category: 'cardiovascular',
        title: 'Elevated Blood Pressure Risk',
        description: 'Based on recent trends, your blood pressure may exceed normal range within 2 weeks.',
        confidence: 87,
        severity: 'high',
        predictedDate: new Date('2025-02-01'),
        currentValue: 135,
        predictedValue: 145,
        unit: 'mmHg',
        recommendations: [
          'Reduce sodium intake to less than 2,300mg per day',
          'Increase physical activity to 150 minutes per week',
          'Practice stress management techniques',
          'Monitor blood pressure daily'
        ],
        dataPoints: [
          { date: new Date('2025-01-01'), value: 120 },
          { date: new Date('2025-01-08'), value: 125 },
          { date: new Date('2025-01-15'), value: 135 },
          { date: new Date('2025-01-22'), value: 140 },
          { date: new Date('2025-02-01'), value: 145 }
        ],
        isActive: true,
        createdAt: new Date('2025-01-15')
      },
      {
        id: '2',
        type: 'opportunity',
        category: 'fitness',
        title: 'Optimal Exercise Window',
        description: 'Your energy levels and recovery patterns suggest the best time for high-intensity workouts.',
        confidence: 92,
        severity: 'low',
        predictedDate: new Date('2025-01-25'),
        recommendations: [
          'Schedule intense workouts between 6-8 AM',
          'Focus on strength training during this period',
          'Include 20 minutes of cardio',
          'Allow 48 hours between high-intensity sessions'
        ],
        dataPoints: [
          { date: new Date('2025-01-01'), value: 65 },
          { date: new Date('2025-01-08'), value: 70 },
          { date: new Date('2025-01-15'), value: 75 },
          { date: new Date('2025-01-22'), value: 80 },
          { date: new Date('2025-01-25'), value: 85 }
        ],
        isActive: true,
        createdAt: new Date('2025-01-10')
      },
      {
        id: '3',
        type: 'trend',
        category: 'nutrition',
        title: 'Blood Sugar Stability Improvement',
        description: 'Your dietary changes are showing positive effects on blood sugar stability.',
        confidence: 78,
        severity: 'low',
        predictedDate: new Date('2025-02-15'),
        currentValue: 120,
        predictedValue: 110,
        unit: 'mg/dL',
        recommendations: [
          'Continue current meal timing pattern',
          'Maintain protein intake at 20-25% of calories',
          'Include fiber-rich foods with each meal',
          'Monitor post-meal glucose levels'
        ],
        dataPoints: [
          { date: new Date('2025-01-01'), value: 140 },
          { date: new Date('2025-01-08'), value: 135 },
          { date: new Date('2025-01-15'), value: 130 },
          { date: new Date('2025-01-22'), value: 125 },
          { date: new Date('2025-02-15'), value: 110 }
        ],
        isActive: true,
        createdAt: new Date('2025-01-05')
      }
    ];
    setPredictions(mockPredictions);
  }, []);

  const filteredPredictions = predictions.filter(prediction => {
    const matchesSearch = prediction.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         prediction.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || prediction.category === categoryFilter;
    const matchesSeverity = severityFilter === 'all' || prediction.severity === severityFilter;
    
    return matchesSearch && matchesCategory && matchesSeverity;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'risk': return 'bg-red-100 text-red-800';
      case 'opportunity': return 'bg-green-100 text-green-800';
      case 'trend': return 'bg-blue-100 text-blue-800';
      case 'alert': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cardiovascular': return <Heart className="h-5 w-5" />;
      case 'diabetes': return <Activity className="h-5 w-5" />;
      case 'mental-health': return <Brain className="h-5 w-5" />;
      case 'nutrition': return <Target className="h-5 w-5" />;
      case 'fitness': return <TrendingUp className="h-5 w-5" />;
      case 'sleep': return <Eye className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const predictionStats = {
    total: predictions.length,
    active: predictions.filter(p => p.isActive).length,
    risks: predictions.filter(p => p.type === 'risk').length,
    opportunities: predictions.filter(p => p.type === 'opportunity').length,
    highConfidence: predictions.filter(p => p.confidence >= 80).length,
    criticalSeverity: predictions.filter(p => p.severity === 'critical').length
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Predictive Health Insights</h1>
          <p className="text-slate-600">AI-powered predictions and health trend analysis</p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg transition-colors"
          >
            ← Back
          </button>
        )}
      </div>

      {/* Prediction Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Predictions</p>
              <p className="text-2xl font-bold text-slate-800">{predictionStats.total}</p>
            </div>
            <TrendingUp className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Active Insights</p>
              <p className="text-2xl font-bold text-slate-800">{predictionStats.active}</p>
            </div>
            <Activity className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Risk Alerts</p>
              <p className="text-2xl font-bold text-slate-800">{predictionStats.risks}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Opportunities</p>
              <p className="text-2xl font-bold text-slate-800">{predictionStats.opportunities}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">High Confidence</p>
              <p className="text-2xl font-bold text-slate-800">{predictionStats.highConfidence}</p>
            </div>
            <Shield className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Critical Alerts</p>
              <p className="text-2xl font-bold text-slate-800">{predictionStats.criticalSeverity}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('predictions')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'predictions'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Predictions
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Analytics
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Settings
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Health Insights Overview</h3>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100">Prediction Accuracy</p>
                      <p className="text-3xl font-bold">94.2%</p>
                    </div>
                    <Target className="h-12 w-12 text-blue-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100">Health Score</p>
                      <p className="text-3xl font-bold">87/100</p>
                    </div>
                    <Heart className="h-12 w-12 text-green-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100">Next Check-in</p>
                      <p className="text-3xl font-bold">3 days</p>
                    </div>
                    <Calendar className="h-12 w-12 text-purple-200" />
                  </div>
                </div>
              </div>

              {/* Recent Predictions */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-4">Recent Predictions</h4>
                <div className="space-y-3">
                  {predictions.slice(0, 3).map((prediction) => (
                    <div key={prediction.id} className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3">
                          {getCategoryIcon(prediction.category)}
                          <div>
                            <h5 className="font-medium text-slate-800">{prediction.title}</h5>
                            <p className="text-sm text-slate-600">{prediction.description}</p>
                            <div className="flex items-center space-x-2 mt-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(prediction.type)}`}>
                                {prediction.type}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(prediction.severity)}`}>
                                {prediction.severity}
                              </span>
                              <span className="text-xs text-slate-500">
                                {prediction.confidence}% confidence
                              </span>
                            </div>
                          </div>
                        </div>
                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                          View Details
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'predictions' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search predictions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="cardiovascular">Cardiovascular</option>
                  <option value="diabetes">Diabetes</option>
                  <option value="mental-health">Mental Health</option>
                  <option value="nutrition">Nutrition</option>
                  <option value="fitness">Fitness</option>
                  <option value="sleep">Sleep</option>
                </select>
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Severity</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              {/* Predictions List */}
              <div className="space-y-4">
                {filteredPredictions.map((prediction) => (
                  <div key={prediction.id} className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start space-x-3 mb-4">
                          {getCategoryIcon(prediction.category)}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-slate-800 mb-2">{prediction.title}</h3>
                            <p className="text-slate-600 mb-3">{prediction.description}</p>
                            
                            <div className="flex items-center space-x-4 mb-3">
                              <span className={`px-2 py-1 text-xs rounded-full ${getTypeColor(prediction.type)}`}>
                                {prediction.type}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(prediction.severity)}`}>
                                {prediction.severity}
                              </span>
                              <span className="text-sm text-slate-500">
                                {prediction.confidence}% confidence
                              </span>
                              <span className="text-sm text-slate-500">
                                Predicted: {formatDate(prediction.predictedDate)}
                              </span>
                            </div>

                            {prediction.currentValue && prediction.predictedValue && (
                              <div className="bg-slate-50 rounded-lg p-3 mb-3">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm text-slate-600">Current: {prediction.currentValue} {prediction.unit}</span>
                                  <span className="text-sm text-slate-600">Predicted: {prediction.predictedValue} {prediction.unit}</span>
                                </div>
                              </div>
                            )}

                            <div>
                              <h4 className="font-medium text-slate-800 mb-2">Recommendations:</h4>
                              <ul className="space-y-1">
                                {prediction.recommendations.slice(0, 3).map((rec, index) => (
                                  <li key={index} className="text-sm text-slate-600 flex items-start">
                                    <span className="text-blue-500 mr-2">•</span>
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 ml-4">
                        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                          <BarChart3 className="h-4 w-4" />
                          <span>View Details</span>
                        </button>
                        
                        <div className="flex flex-col space-y-1">
                          <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                            <Share2 className="h-4 w-4" />
                          </button>
                          <button className="p-2 text-slate-400 hover:text-slate-600 transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredPredictions.length === 0 && (
                  <div className="text-center py-12">
                    <TrendingUp className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">No predictions found</h3>
                    <p className="text-slate-600">No predictions match your current filters.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Prediction Analytics</h3>
              
              {/* Analytics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Accuracy Rate</p>
                      <p className="text-2xl font-bold text-slate-800">94.2%</p>
                    </div>
                    <Target className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Predictions Made</p>
                      <p className="text-2xl font-bold text-slate-800">1,247</p>
                    </div>
                    <TrendingUp className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Risk Averted</p>
                      <p className="text-2xl font-bold text-slate-800">89</p>
                    </div>
                    <Shield className="h-8 w-8 text-red-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Opportunities</p>
                      <p className="text-2xl font-bold text-slate-800">156</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                </div>
              </div>

              {/* Charts Placeholder */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-4">Prediction Accuracy Over Time</h4>
                  <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center">
                    <LineChart className="h-12 w-12 text-slate-400" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-semibold text-slate-800 mb-4">Category Distribution</h4>
                  <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center">
                    <PieChart className="h-12 w-12 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Prediction Settings</h3>
              
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h4 className="font-medium text-slate-800 mb-4">Notification Preferences</h4>
                <div className="space-y-4">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span className="text-sm text-slate-700">High-risk predictions</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span className="text-sm text-slate-700">Opportunity alerts</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded" />
                    <span className="text-sm text-slate-700">Weekly prediction summaries</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" defaultChecked className="rounded" />
                    <span className="text-sm text-slate-700">Email notifications</span>
                  </label>
                </div>
              </div>

              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h4 className="font-medium text-slate-800 mb-4">Prediction Sensitivity</h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Confidence Threshold</label>
                    <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="70">70% (More predictions)</option>
                      <option value="80" selected>80% (Balanced)</option>
                      <option value="90">90% (Fewer, more accurate)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Time Range</label>
                    <select 
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value as any)}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="7d">7 days</option>
                      <option value="30d">30 days</option>
                      <option value="90d">90 days</option>
                      <option value="1y">1 year</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Save Settings
                </button>
                <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-lg transition-colors">
                  Reset to Default
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictiveInsights; 